import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { logError } from '@/lib/sentry';
import {
  bulkUpsertInventoryItems,
  createInventoryItem,
  recordInventoryMovement,
  softDeleteInventoryItem,
  softDeleteInventoryItems,
  updateInventoryItem,
} from '@/supabase/inventory/service/inventoryService';
import {
  isUnauthenticatedInventoryError,
  isDuplicateInventoryItemError,
  isInvalidItemNameError,
  isInvalidInventoryArgumentError,
  isInventoryItemNotFoundError,
  isBulkUpsertValidationError,
} from '@/supabase/inventory/inventoryErrors';
import type {
  BulkUpsertItemPayload,
  BulkUpsertMode,
  BulkUpsertResult,
  CreateInventoryItemInput,
  RecordMovementInput,
  UpdateInventoryItemInput,
} from '@/supabase/inventory/types/inventory';

export interface UseInventoryActionsProps {
  onMutated: () => Promise<void> | void;
}

export interface UseInventoryActionsResult {
  createItem: (input: CreateInventoryItemInput) => Promise<boolean>;
  updateItem: (input: UpdateInventoryItemInput) => Promise<boolean>;
  recordMovement: (input: RecordMovementInput) => Promise<boolean>;
  softDeleteItem: (id: number) => Promise<boolean>;
  softDeleteItems: (ids: number[]) => Promise<boolean>;
  bulkUpsert: (items: BulkUpsertItemPayload[], mode: BulkUpsertMode) => Promise<BulkUpsertResult | null>;
  isSubmitting: boolean;
}

// service가 던지는 에러(서버 RPC ERRCODE·클라 사전검증 둘 다 동일 어휘)를 사용자 메시지로 매핑.
function mapInventoryErrorMessage(err: unknown): string {
  if (isUnauthenticatedInventoryError(err)) return '로그인이 필요합니다';
  if (isDuplicateInventoryItemError(err)) return '이미 등록된 상품입니다';
  if (isInvalidItemNameError(err)) return '상품명을 입력해 주세요';
  if (isBulkUpsertValidationError(err)) return '파일 내용을 확인한 뒤 다시 시도해 주세요';
  if (isInvalidInventoryArgumentError(err)) return '입력값을 확인해 주세요';
  if (isInventoryItemNotFoundError(err)) return '상품을 찾을 수 없습니다. 새로고침 후 다시 시도해 주세요';
  return '잠시 후 다시 시도해 주세요';
}

function movementSuccessMessage(input: RecordMovementInput): string {
  if (input.type === '입고') return `입고 ${input.qty}개를 기록했습니다`;
  if (input.type === '출고') return `출고 ${input.qty}개를 기록했습니다`;
  return '재고를 조정했습니다';
}

// PR5 엑셀 일괄등록 성공 토스트 — 0인 항목은 생략(예: 신규 파일이면 갱신/복원/삭제 전부 0).
// 전부 0이면(이론상 — 유효 행 0개는 모달이 실행 전 차단하지만 방어적으로) 담백한 문구로 폴백.
function bulkUpsertSuccessMessage(result: BulkUpsertResult): string {
  const parts: string[] = [];
  if (result.created > 0) parts.push(`${result.created}개 등록`);
  if (result.updated > 0) parts.push(`${result.updated}개 갱신`);
  if (result.restored > 0) parts.push(`${result.restored}개 복원`);
  if (result.softDeleted > 0) parts.push(`${result.softDeleted}개 삭제`);
  return parts.length > 0 ? parts.join(' · ') : '변경 사항이 없습니다';
}

/**
 * 재고 액션(등록 / 입출고·조정 / 소프트삭제) 도메인 훅(PR2 Task2-B). service·에러 predicate·타입만
 * import(repository·supabase client 직접 import 금지).
 *
 * 각 액션: isSubmitting 이중 제출 가드(동기 ref로 판정 — state는 UI 표시용일 뿐 가드 자체에는 쓰지
 * 않는다. state를 가드에 쓰면 콜백 deps에 넣어야 해 참조가 매 제출마다 바뀌어 memo 자식이 깨진다) →
 * service 호출 → 성공 토스트 → await onMutated() → true. 실패: false 반환(모달은 호출부가 열린 채
 * 유지) + 에러 토스트.
 */
export function useInventoryActions({ onMutated }: UseInventoryActionsProps): UseInventoryActionsResult {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const createItem = useCallback(async (input: CreateInventoryItemInput): Promise<boolean> => {
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await createInventoryItem(input);
      toast.success('상품을 등록했습니다');
      await onMutated();
      return true;
    } catch (err) {
      logError('useInventoryActions.createItem', err);
      toast.error(mapInventoryErrorMessage(err));
      return false;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [onMutated]);

  const updateItem = useCallback(async (input: UpdateInventoryItemInput): Promise<boolean> => {
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await updateInventoryItem(input);
      toast.success('상품 정보를 저장했습니다');
      await onMutated();
      return true;
    } catch (err) {
      logError('useInventoryActions.updateItem', err);
      toast.error(mapInventoryErrorMessage(err));
      return false;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [onMutated]);

  const recordMovement = useCallback(async (input: RecordMovementInput): Promise<boolean> => {
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await recordInventoryMovement(input);
      toast.success(movementSuccessMessage(input));
      await onMutated();
      return true;
    } catch (err) {
      logError('useInventoryActions.recordMovement', err);
      toast.error(mapInventoryErrorMessage(err));
      return false;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [onMutated]);

  const softDeleteItem = useCallback(async (id: number): Promise<boolean> => {
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await softDeleteInventoryItem(id);
      toast.success('상품을 삭제했습니다');
      await onMutated();
      return true;
    } catch (err) {
      logError('useInventoryActions.softDeleteItem', err);
      toast.error(mapInventoryErrorMessage(err));
      return false;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [onMutated]);

  // 일괄 삭제 — softDeleteItem의 파이프라인 클론. 토스트 개수는 서버가 실제로 삭제한 반환값
  // 기준(요청 개수 아님 — 다른 탭에서 먼저 삭제된 행이 섞이면 요청보다 적을 수 있고, 그때
  // 요청 개수를 말하면 거짓말이 된다).
  const softDeleteItems = useCallback(async (ids: number[]): Promise<boolean> => {
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const count = await softDeleteInventoryItems(ids);
      toast.success(`상품 ${count.toLocaleString('ko-KR')}개를 삭제했습니다`);
      await onMutated();
      return true;
    } catch (err) {
      logError('useInventoryActions.softDeleteItems', err);
      toast.error(mapInventoryErrorMessage(err));
      return false;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [onMutated]);

  // 다른 액션과 달리 성공해도 boolean이 아니라 result(카운트 5키)를 반환한다 — 호출부(모달)가
  // 결과 요약 스텝을 렌더해야 해서, "성공했다"는 사실만으로는 부족하다(실패는 null).
  const bulkUpsert = useCallback(
    async (items: BulkUpsertItemPayload[], mode: BulkUpsertMode): Promise<BulkUpsertResult | null> => {
      if (isSubmittingRef.current) return null;
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      try {
        const result = await bulkUpsertInventoryItems(items, mode);
        toast.success(bulkUpsertSuccessMessage(result));
        await onMutated();
        return result;
      } catch (err) {
        logError('useInventoryActions.bulkUpsert', err);
        toast.error(mapInventoryErrorMessage(err));
        return null;
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [onMutated],
  );

  return { createItem, updateItem, recordMovement, softDeleteItem, softDeleteItems, bulkUpsert, isSubmitting };
}
